import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { apiError, clearTokens, hasSession, setTokens } from '../../api/client'
import { services } from '../../api/services'
import type { User } from '../../types/api'

type AuthValue = { user: User | null; ready: boolean; sessionError: string; retrySession: () => Promise<void>; login: (phone: string, password: string) => Promise<void>; logout: () => void; isRechargeur: boolean; isParieur: boolean }
const AuthContext = createContext<AuthValue | null>(null)
const USER_KEY = 'yl_user'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)
  const [sessionError, setSessionError] = useState('')
  const sequence=useRef(0)
  const retrySession = async () => {
    const current=++sequence.current
    setReady(false); setSessionError('')
    if (!hasSession()) { setUser(null); setReady(true); return }
    try {
      const cached = JSON.parse(localStorage.getItem(USER_KEY) || 'null') as User | null
      if (!cached?.phone) { clearTokens(); setUser(null); return }
      const {data} = await services.wallet()
      const identity: User = {id:cached.id, phone:cached.phone, email:cached.email, first_name:cached.first_name, last_name:cached.last_name, groupes:data.groupes, role_principal:data.role_principal, wallet_mode:data.wallet_mode}
      if (hasSession()&&current===sequence.current) { setUser(identity); localStorage.setItem(USER_KEY,JSON.stringify(identity)) }
    } catch (error) { if(current===sequence.current){setUser(null); if(hasSession()) setSessionError(apiError(error))} }
    finally { if(current===sequence.current)setReady(true) }
  }
  useEffect(() => { void retrySession() }, [])
  useEffect(() => { const expired = () => { sequence.current++;setUser(null);clearTokens();setSessionError('');setReady(true) }; window.addEventListener('youloto:session-expired', expired); return () => window.removeEventListener('youloto:session-expired', expired) }, [])
  const login = async (phone: string, password: string) => {
    const current=++sequence.current
    const { data } = await services.login(phone, password)
    if(current!==sequence.current)return
    setTokens(data.access, data.refresh)
    const next: User = { id:data.id, phone:data.phone, email:data.email, first_name:data.first_name, last_name:data.last_name, groupes:data.groupes || [], role_principal:data.role_principal, wallet_mode:data.wallet_mode }
    localStorage.setItem(USER_KEY, JSON.stringify(next)); setUser(next);setReady(true);setSessionError('')
  }
  const logout = () => { sequence.current++;clearTokens(); setUser(null); setSessionError('');setReady(true) }
  const value = useMemo(() => ({ user, ready, sessionError, retrySession, login, logout, isRechargeur: Boolean(user?.groupes?.includes('RECHARGEURS')), isParieur: Boolean(user?.groupes?.includes('PARIEURS')) }), [user, ready, sessionError])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
export function useAuth() { const value = useContext(AuthContext); if (!value) throw new Error('AuthProvider manquant'); return value }
